import React, { useContext, useEffect, useState } from "react";

import { Button, Empty, Input, message, Modal, Table } from "antd";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useHistory } from "react-router-dom";
import useSWR from "swr";
import { SessionContext } from "../../App";
import {
  GetEmailTemplateByCompanyIdResponse,
  GetEmailTemplateVariablesResponse,
  UpdateEmailTemplateRequest
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import {
  EmailTemplate
} from "../../constants/types";
import {
  fetcher,
  updateEmailTemplate
} from "../../services/api";
import { EditEmailTemplatePageKeys } from "../../services/i8tn/EditEmailTemplatePage/keys";

interface Props {
  emailTemplateId: string
  isShowFooter: boolean;
  onUpdate?: (editorState: string | undefined) => void;
}

export default function EditEmailTemplateForm(props: Props) {
  const context = useContext(SessionContext);
  const history = useHistory();

  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<
    EmailTemplate | undefined
  >();
  const [editorState, setEditorState] = useState<string | undefined>();
  
  const { t } = useTranslation();

  const { data: getEmailTemplateRes } =
  useSWR<GetEmailTemplateByCompanyIdResponse>(
    `/emailTemplate/get/${context?.companyUser?.companyId}`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      errorRetryCount: 0,
    }
  );

  const { data: getEmailTemplateVariablesRes } =
  useSWR<GetEmailTemplateVariablesResponse>(
    `/emailTemplate/variables/get`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      errorRetryCount: 0,
    }
  );
  
  useEffect(() => {
    if(!props.emailTemplateId) return 
    const et = getEmailTemplateRes?.emailTemplates?.find(et => {
      return et._id === props.emailTemplateId;
    })
    setEmailTemplate(et)
    setEditorState(et?.content);
  }, [props.emailTemplateId, getEmailTemplateRes]);

  const onSave = () => {
    if (!emailTemplate) return null;

    setIsUpdateLoading(true);

    emailTemplate.content = editorState || "";

    const req: UpdateEmailTemplateRequest = {
      emailTemplate: emailTemplate,
    };

    updateEmailTemplate(req)
      .then((res) => {
        if (!res.success) throw new Error(res.message);

        message.success("Updated");
        history.push("/settings");
      })
      .catch((err) => message.error(err))
      .finally(() => setIsUpdateLoading(false));
  };

  const dataSource = Object.entries(getEmailTemplateVariablesRes?.emailTemplateVariables || {}).map(([key, value]) => {
    return {
      key: key,
      ...value,
    };
  })

  return (
    <div>
      {emailTemplate ? (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 30,
            }}
          >
            <div
              style={{
                flex: 4,
              }}
            >
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  gap: 10,
                  flexDirection: "column",
                }}
              >
                <span>
                  <b>
                    {t(EditEmailTemplatePageKeys.editEmailTemplate_nameString)}
                  </b>
                  <p>{emailTemplate.name}</p>
                </span>
                <span>
                  <b>
                    {t(
                      EditEmailTemplatePageKeys.editEmailTemplate_subjectString
                    )}
                  </b>
                  <Input
                    data-testid={
                      TEST_IDS.EDIT_EMAIL_TEMPLATE_PAGE.subject_input_id
                    }
                    value={emailTemplate.subject}
                    onChange={(e) => {
                      setEmailTemplate({
                        ...emailTemplate,
                        subject: e.target.value,
                      });
                    }}
                  />
                </span>

                <span>
                  <b>
                    {t(
                      EditEmailTemplatePageKeys.editEmailTemplate_contentString
                    )}
                  </b>
                  <ReactQuill
                    data-testid={
                      TEST_IDS.EDIT_EMAIL_TEMPLATE_PAGE.content_input_id
                    }
                    style={{ height: "80%" }}
                    theme="snow"
                    value={editorState}
                    onChange={(v) => {
                      setEditorState(v);
                      props.onUpdate && props.onUpdate(editorState);
                    }}
                  />
                </span>
              </div>
            </div>

            <div
              style={{
                flex: 2,
              }}
            >
              <b>
                {t(EditEmailTemplatePageKeys.editEmailTemplate_variablesString)}
              </b>
              <p>
                <i>
                  {t(
                    EditEmailTemplatePageKeys.editEmailTemplate_introDynamicValuesString
                  )}
                </i>
              </p>
              <Table
                dataSource={dataSource}
                size="small"
                bordered
                columns={[
                  {
                    title: (
                      <>
                        {t(
                          EditEmailTemplatePageKeys.editEmailTemplate_variableNameTableHeader
                        )}
                      </>
                    ),
                    dataIndex: "key",
                    render: (value) => <i>{value}</i>,
                  },
                  {
                    title: (
                      <>
                        {t(
                          EditEmailTemplatePageKeys.editEmailTemplate_descriptionTableHeader
                        )}
                      </>
                    ),
                    dataIndex: "description",
                  },
                ]}
                pagination={false}
              />
            </div>
          </div>
          {props.isShowFooter ? (
            <div
              style={{
                display: "flex",
                gap: 20,
                paddingTop: 10,
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button
                loading={isUpdateLoading}
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    content: (
                      <p>
                        {t(
                          EditEmailTemplatePageKeys.editEmailTemplate_savePrompt
                        )}
                      </p>
                    ),
                    onOk: () => onSave(),
                  });
                }}
              >
                <>{t(EditEmailTemplatePageKeys.editEmailTemplate_saveBtn)}</>
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <Empty />
      )}
    </div>
  );
}
